import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

export class FormUtils {

  static clearAllValidators(form: AbstractControl): void {
    if (form instanceof UntypedFormGroup) {
      this.clearGroupValidators(form);
    } else if (form instanceof UntypedFormArray) {
      this.clearArrayValidators(form);
    } else if (form instanceof UntypedFormControl) {
      this.clearControlValidators(form);
    }
  }

  private static clearControlValidators(form: UntypedFormControl): void {
    form.clearValidators();
  }

  private static clearGroupValidators(form: UntypedFormGroup): void {
    Object.keys(form.controls)
      .forEach(key => this.clearAllValidators(form.controls[key]));
  }

  private static clearArrayValidators(form: UntypedFormArray): void {
    form.controls.forEach(control => this.clearAllValidators(control));
  }

  static clearFormArray(form: UntypedFormArray): void {
    while (form.length !== 0) {
      form.removeAt(0);
    }
  }

  static showFormValidationErrors(formControl: AbstractControl): void {
    if (formControl instanceof UntypedFormControl) {
      formControl.markAsTouched({ onlySelf: true });
    } else if (formControl instanceof UntypedFormGroup || formControl instanceof UntypedFormArray) {
      this.showFormControlsValidationErrors(formControl);
    }
  }

  private static showFormControlsValidationErrors(formControl: UntypedFormGroup | UntypedFormArray): void {
    Object.keys(formControl.controls).forEach(field => {
      const control = formControl.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
        this.showFormValidationErrors(control);
      }
    });
  }

  static flattenForm(controls: AbstractControl[]): AbstractControl[] {
    return [].concat(
      ...controls.map(control => {
        if (control instanceof UntypedFormGroup) {
          return this.flattenForm(Object.values(control.controls));
        }
        if (control instanceof UntypedFormArray) {
          return this.flattenForm(control.controls);
        } else {
          return control;
        }
      }
      )
    );
  }

  static getInvalidControls(formGroup: UntypedFormGroup): AbstractControl[] {
    return this.flattenForm(Object.values(formGroup.controls))
      .filter(control => control.errors);
  }

  static focusFirstRequired(formControl: AbstractControl): void {
    if (formControl instanceof UntypedFormControl) {
      this.focusFormControl(formControl);
    } else if (formControl instanceof UntypedFormGroup || formControl instanceof UntypedFormArray) {
      this.focusFirstRequiredFormControl(formControl);
    }
  }

  private static focusFirstRequiredFormControl(formControl: UntypedFormGroup | UntypedFormArray): void {
    const visibleControls = this.flattenForm(Object.values(formControl.controls))
      .filter(control => this.isRequired(control))
      .sort(this.sortByDOMPosition);

    const requiredControl = visibleControls[0] as any;
    if (requiredControl) {
      this.focusElement(requiredControl.nativeElement);
    }
  }

  private static focusFormControl(formControl: UntypedFormControl): void {
    if (this.isRequired(formControl)) {
      this.focusElement((formControl as any).nativeElement);
    }
  }

  private static isRequired(formControl: AbstractControl): boolean {
    return formControl.errors && formControl.errors['required'] && !!(formControl as any).nativeElement;
  }

  static sortByDOMPosition(a: any, b: any): number {
    const aRect: ClientRect | DOMRect = (a.nativeElement as HTMLElement).getBoundingClientRect();
    const bRect: ClientRect | DOMRect = (b.nativeElement as HTMLElement).getBoundingClientRect();

    if (Math.abs(aRect.top - bRect.top) > 5) {
      return aRect.top - bRect.top;
    }
    return aRect.left - bRect.left;
  }

  private static focusElement(element: HTMLElement): void {
    const top = element.getBoundingClientRect().top;
    if (top > 0 && top < 100) {
      window.scrollBy(0, -200);
    }

    element.focus();
  }
}
